// CLIO screen help (the "?" at the top of CLIO, "My Journal"). Plain words; keep it in step
// with the screen (apps/mobile/src/tools/ClioTool.tsx): section names, buttons and labels
// must match what the screen shows.
import type { ScreenHelpContent } from '../../v10/ScreenHelp';

export const clioHelp: ScreenHelpContent = {
  title: 'CLIO · My Journal',
  intro:
    "CLIO is where you close your day. Say how it went, look back at what you planned, and write down what happened and what it taught you. Each day has its own page, so you can also go back and fill in an earlier one.",
  sections: [
    {
      heading: 'Pick the day',
      body: [
        'Use the ‹ and › arrows around the date to move one day back or forward. Everything on the screen switches to that day.',
      ],
    },
    {
      heading: 'How did it go?',
      body: [
        'Tap Complete, Partial or Incomplete to say how the day went. Then you can add a short note, or tap Skip note to clear it.',
        'Press Save to keep it. The button reads Saving… and then Saved. If you change your choice or the note, press Save again. This is saved on its own, separate from the journal entry below.',
      ],
    },
    {
      heading: 'EPIC',
      body: [
        "The story of the day, written for you as an episode. If there is none yet, press Generate today's episode. Once it is there you see its number, title and text.",
        'Press Generate audio to turn it into a recording, then Play or Pause to listen.',
      ],
    },
    {
      heading: 'Parts that show up for some entry types',
      body: [
        'Which of these you see depends on the entry type you picked and how it is set up in Settings. If one is not there, that type has it turned off.',
      ],
      items: [
        { term: 'Quote card', text: 'your guiding principles, the short rules you chose to be reminded of.' },
        { term: 'Monthly Goals / Weekly Goals', text: 'the goals you picked for this entry type. They are for reading here; finished ones are crossed out.' },
        { term: 'TKDT', text: 'tap the heading to open it. Tap an item to add 1 to its count. Type a name and press + to add a new item. These save as soon as you tap.' },
        { term: "Today's Itinerary", text: "the calendar events and scheduled tasks for the day you are on, in time order. Done marks a task finished, or removes a calendar event from the calendar. Started asks for a reason, saves it, then moves the item to a later time. Reschedule moves a task to its next free slot, starting tomorrow. For a calendar event it asks you for a new date (yyyy-MM-dd) and time (HH:mm)." },
        { term: 'T.E.L.O.S', text: 'one card for each of your active areas. Tap Did the work or Missed it to mark how that area went. The cycle itself is moved on in the TELOS tool, not here.' },
        { term: '🔔 bell', text: 'at the top right. The number shows tasks the app scheduled for you that are waiting for your OK. In the list, Keep accepts one, Keep All accepts every one, and Reschedule lets you type a new date and time. Switch between List and Grid (a week view) at the top. Press Done to close.' },
      ],
    },
    {
      heading: 'Write the entry',
      body: [
        'The buttons above the questions are your entry types, for example a morning one or an evening one. Tap one to switch. CLIO remembers your last choice.',
        'Under "Prompts for ..." answer each question. Some are a box to write in, some are a row of choices to tap. If a type has no questions, it says "Free write!".',
        'Press Save Entry. After the first time the button reads Save Changes. If the entry was changed somewhere else, CLIO warns you instead of overwriting it.',
        'Some types also show a word count. It may have a target to reach.',
      ],
    },
    {
      heading: 'EP',
      body: [
        'The small EP badge next to the word count decides whether this entry feeds your EPIC episode. Lit means it is included. Tap it to turn it off, and the screen says "Private". It is saved when you press Save Entry.',
      ],
    },
    {
      heading: 'Additional entries',
      body: [
        'Some entry types let you add extra notes to the same day. The section only appears after the main entry is saved. Press + ADDITIONAL, write, and press Save on that note. You can come back and edit a saved note, then press its Save again.',
      ],
    },
  ],
};
